import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import LoginPage from '@/pages/LoginPage';
import StudentDashboard from '@/pages/StudentDashboard';
import ParentDashboard from '@/pages/ParentDashboard';
import SuperuserDashboard from '@/pages/SuperuserDashboard';
import TeacherDashboard from '@/pages/TeacherDashboard';
import CounselorDashboard from '@/pages/CounselorDashboard';
import InstitutionRegistration from '@/pages/InstitutionRegistration';
import InstitutionLogin from '@/pages/InstitutionLogin';
import InstitutionDashboard from '@/pages/InstitutionDashboard';
import PrivacySettings from '@/pages/PrivacySettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/" />;
};

function AppRoutes() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('userId', userData.id);
    setUser(userData);
    
    // Navigate based on role
    if (userData.role === 'student') {
      navigate('/student');
    } else if (userData.role === 'parent') {
      navigate('/parent');
    } else if (userData.role === 'teacher') {
      navigate('/teacher');
    } else if (userData.role === 'counselor') {
      navigate('/counselor');
    } else if (userData.role === 'superuser') {
      navigate('/superuser');
    } else if (userData.role === 'institution') {
      navigate('/institution-dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    setUser(null);
    navigate('/');
  };

  return (
    <Routes>
          <Route
            path="/"
            element={
              user ? (
                user.role === 'student' ? (
                  <Navigate to="/student" />
                ) : user.role === 'parent' ? (
                  <Navigate to="/parent" />
                ) : user.role === 'teacher' ? (
                  <Navigate to="/teacher" />
                ) : user.role === 'counselor' ? (
                  <Navigate to="/counselor" />
                ) : user.role === 'superuser' ? (
                  <Navigate to="/superuser" />
                ) : (
                  <Navigate to="/" />
                )
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/student"
            element={
              <ProtectedRoute>
                <StudentDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent"
            element={
              <ProtectedRoute>
                <ParentDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher"
            element={
              <ProtectedRoute>
                <TeacherDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/counselor"
            element={
              <ProtectedRoute>
                <CounselorDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser"
            element={
              <ProtectedRoute>
                <SuperuserDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route path="/institution-register" element={<InstitutionRegistration />} />
          <Route path="/institution-login" element={<InstitutionLogin onLogin={handleLogin} />} />
          <Route
            path="/institution-dashboard"
            element={
              <ProtectedRoute>
                <InstitutionDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              <ProtectedRoute>
                <PrivacySettings user={user} onBack={() => window.history.back()} />
              </ProtectedRoute>
            }
          />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
